import type { ResolvedMonospaceCollection, ResolvedMonospaceDataSource } from '#layers/data-model/types/schema/resolved-schema';
import type { DataSourceConnectorRef } from '#layers/data-model/utils/data-source-connector-lookup';
import { useDataSourceConnectors } from '#layers/data-model/composables/use-data-source-connectors';
import { ConnectorSource } from '#layers/data-model/types/data-connectors/define';

export function useSchemaEditability() {
	const { getDataSourceConnector } = useDataSourceConnectors();

	/**
	 * Whether Studio offers schema changes (creating, altering or removing collections, fields,
	 * relations and indexes) for a data source. Monospace-only metadata stays editable either way,
	 * including API names: renaming one without its database name never reaches the connector.
	 *
	 * Stand-in until the engine transmits its `SchemaMigrations` connector capability: extension
	 * connectors can't migrate their schema, so only sources backed by a built-in connector qualify.
	 * A source with a locked schema refuses these changes too.
	 */
	function isSchemaEditable(source: DataSourceConnectorRef & Pick<ResolvedMonospaceDataSource, 'schemaLocked'>) {
		return !source.schemaLocked && getDataSourceConnector(source)?.source === ConnectorSource.Builtin;
	}

	return {
		isSchemaEditable,
		isCollectionSchemaEditable: (collection: Pick<ResolvedMonospaceCollection, 'source'>) =>
			!!collection.source && isSchemaEditable(collection.source),
	};
}
